import { baseCleanProps, jobIncludes, jobOverviewIncludes, listParams } from "common/api/Api";
import { fetcher, IGlobalRequestOptions } from "common/api/Fetcher";
import { downloadFile } from "common/helpers/File";
import { IJobData, JobRecord } from "common/records/Job";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { ITaskData } from "common/records/Task";
import { JobParamsRecord } from "common/records/index/JobParams";
import { toParams } from "common/records/index/QueryParamsBase";

const jobUrl = "api/v1/jobs";
const orgUrl = "api/v1/orgs";

export interface IJobOptions {
  org_id: number;
  id: number;
}

export interface IJobResponse {
  job: IJobData;
}

export type InviteType = "image_uploader" | "screenshare" | "appointment_invite";
export type InviteMethod = "sms" | "email";

export interface IInviteArgs {
  jobId: number;
  emails: string;
  invite_method: InviteMethod;
  invite_type: InviteType;
  options?: { folder_id: number };
  event_id?: number;
  summary?: string;
}

export interface IJobIndexResponse {
  jobs: IJobData[];
  meta: { pagination: IMetaPaginationData; state_info?: Record<string, { pretty_name: string; color: string }> };
}

export const list = (queryParams: JobParamsRecord, options?: IGlobalRequestOptions): Promise<IJobIndexResponse> => {
  const parameters = toParams(queryParams);
  parameters["include[]"] = jobOverviewIncludes;
  delete parameters.date_range;

  return options ? fetcher.get(jobUrl, parameters, options) : fetcher.get(jobUrl, parameters);
};

export const load = async (id: number): Promise<IJobResponse> => {
  const actionUrl = fetcher.joinUrls(jobUrl, id.toString());
  return await fetcher.get<IJobResponse>(actionUrl, { "include[]": jobIncludes });
};

export const invite = async (args: IInviteArgs): Promise<{ task: ITaskData }> => {
  const actionUrl = fetcher.joinUrls(jobUrl, args.jobId.toString(), "invite");

  return await fetcher.post(actionUrl, args);
};

export const loadSamples = async (org_id: number): Promise<{ sample_jobs: string[] }> => {
  const url = fetcher.joinUrls(jobUrl, "sample_list");
  return await fetcher.get(url, { org_id });
};

export const createSample = async (
  org_id: number,
  user_assigned_id: number,
  sample_types: string[],
): Promise<{ task: ITaskData }> => {
  const url = fetcher.joinUrls(jobUrl, "sample");
  return await fetcher.post(url, { org_id, user_assigned_id, sample_types });
};

export const update = async (job: JobRecord, options?: IGlobalRequestOptions): Promise<IJobResponse> => {
  const actionUrl = fetcher.joinUrls(jobUrl, job.id.toString());

  if (job.stateAction) {
    await fetcher.patch<IJobResponse>(fetcher.joinUrls(actionUrl, "state"), { event: job.stateAction });
  }
  return options
    ? fetcher.patch<IJobResponse>(actionUrl, { job: cleanProps(job), include: jobIncludes }, options)
    : fetcher.patch<IJobResponse>(actionUrl, { job: cleanProps(job), include: jobIncludes });
};

export const create = async (job: JobRecord): Promise<IJobResponse> => {
  const actionUrl = fetcher.joinUrls(orgUrl, job.org_id.toString(), "jobs");
  return fetcher.post<IJobResponse>(actionUrl, { job: cleanProps(job), include: jobIncludes });
};

export const archive = async (job: JobRecord): Promise<IJobResponse> => {
  const actionUrl = fetcher.joinUrls(orgUrl, job.org_id.toString(), "jobs", job.id.toString());
  return fetcher.delete<IJobResponse>(actionUrl);
};

export const unarchive = async (job: JobRecord): Promise<IJobResponse> => {
  const actionUrl = fetcher.joinUrls(orgUrl, job.org_id.toString(), "jobs", job.id.toString(), "unarchive");
  return fetcher.patch<IJobResponse>(actionUrl);
};

export const exportJobs = async (queryParams: JobParamsRecord): Promise<void> => {
  const parameters = toParams(queryParams);
  delete parameters.date_range;

  const actionUrl = fetcher.urlOptions(jobUrl + ".csv", parameters);
  const filename = `jobs_${queryParams.updated_since.toISOString()}.csv`;
  await downloadFile(actionUrl, filename);
};

export const cleanProps = (job: JobRecord): any => {
  const jobData: any = baseCleanProps(job.toJS());

  if (jobData.phone_numbers?.length > 0) {
    jobData.phone_numbers_attributes = jobData.phone_numbers.map((phone: any) => ({
      id: phone.id <= 0 ? undefined : phone.id,
      name: phone.name,
      number: phone.number,
      // Do not save phone numbers that are empty
      _destroy: !phone.name && !phone.number ? true : phone._destroy,
    }));

    delete jobData.phone_numbers;
  }

  if (jobData.assignments?.length > 0) {
    const assignment = { ...jobData.assignments[0], user_id: jobData.assignments[0].user_id, user: undefined };
    if (assignment.id <= 0) {
      delete assignment.id;
    }
    jobData.assignments_attributes = [assignment];
    delete jobData.assignments;
  }

  jobData.address_attributes = {
    ...jobData.address,
  };
  if (jobData.address_attributes.id <= 0) {
    delete jobData.address_attributes.id;
  }
  delete jobData.address;

  jobData.customer_address_attributes = {
    ...jobData.customer_address,
  };
  if (jobData.customer_address_attributes.id <= 0) {
    delete jobData.customer_address_attributes.id;
  }
  delete jobData.customer_address;

  delete jobData.jobNameChanged;

  return jobData;
};
